import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  createChart,
  ColorType,
  IChartApi,
  ISeriesApi,
  Time,
  LineSeries,
  AreaSeries,
  HistogramSeries,
  CandlestickSeries,
} from 'lightweight-charts';
import ChartShareButton from '../Common/ChartShareButton';

export interface SeriesConfig {
  type: 'line' | 'histogram' | 'area' | 'candlestick';
  data: any[];
  options?: any; // color, lineWidth, etc.
  markers?: any[]; // Optional markers (arrows, circles)
}

interface IndicatorChartProps {
  series: SeriesConfig[];
  height?: number;
  colors?: {
    backgroundColor?: string;
    textColor?: string;
    gridColor?: string;
  };
  title?: string;
  fileName?: string;
}

const IndicatorChart: React.FC<IndicatorChartProps> = ({
  series,
  height = 300,
  colors,
  title,
  fileName = 'indicator_chart',
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const activeColors = {
    backgroundColor: colors?.backgroundColor || (isDark ? '#0a2128' : 'transparent'),
    textColor: colors?.textColor && !colors.textColor.includes('var') ? colors.textColor : (isDark ? '#d3e2e8' : '#333333'),
    gridColor: colors?.gridColor && !colors.gridColor.includes('var') ? colors.gridColor : (isDark ? 'rgba(211, 226, 232, 0.05)' : 'rgba(156, 163, 175, 0.1)'),
  };

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: activeColors.backgroundColor },
        textColor: activeColors.textColor,
      },
      width: chartContainerRef.current.clientWidth,
      height: height,
      grid: {
        vertLines: { color: activeColors.gridColor },
        horzLines: { color: activeColors.gridColor },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
      },
    });

    chartRef.current = chart;

    // Add series
    series.forEach((s) => {
      let chartSeries: ISeriesApi<any>;

      switch (s.type) {
        case 'line':
          chartSeries = chart.addSeries(LineSeries, s.options || {});
          break;
        case 'area':
          chartSeries = chart.addSeries(AreaSeries, s.options || {});
          break;
        case 'histogram':
          chartSeries = chart.addSeries(HistogramSeries, s.options || {});
          break;
        case 'candlestick':
          chartSeries = chart.addSeries(CandlestickSeries, s.options || {});
          break;
        default:
          return;
      }

      chartSeries.setData(s.data);

      if (s.markers) {
        (chartSeries as any).setMarkers(s.markers);
      }
    });

    chart.timeScale().fitContent();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [series, height, colors, theme]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {title && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 2,
            fontSize: '14px',
            fontWeight: 'bold',
            color: activeColors.textColor,
            pointerEvents: 'none',
          }}
        >
          {title}
        </div>
      )}
      <ChartShareButton
        targetRef={wrapperRef}
        fileName={fileName}
        style={{ position: 'absolute', top: 10, right: 70, zIndex: 10 }}
      />
      <div ref={chartContainerRef} style={{ width: '100%', height: `${height}px` }} />
    </div>
  );
};

export default IndicatorChart;

